package com.partejar.app.feature.relacionamentos.presentation

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Icon
import androidx.compose.material3.AlertDialog
import com.partejar.app.feature.contratos.presentation.ContractSetupRequired
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import com.partejar.app.core.ui.components.*
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarShapes
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.feature.relacionamentos.data.InteractionKind
import com.partejar.app.core.session.CurrentWorkspaceRepository
import com.partejar.app.feature.calendar.CalendarEventDraft
import com.partejar.app.feature.calendar.meetingCalendarKey
import com.partejar.app.feature.calendar.presentation.DeviceCalendarSyncDialogs
import com.partejar.app.feature.calendar.presentation.rememberCalendarSyncController
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import org.koin.compose.koinInject

@Composable
fun RelacionamentoDetailScreen(
    id: Long,
    onBack: () -> Unit,
    onEditPerson: () -> Unit,
    reloadKey: Int,
    onStartContract: (Long) -> Unit,
    viewModel: RelacionamentoDetailViewModel = koinInject(),
    onCreatePlan: () -> Unit = {},
    onCreateContractTemplate: () -> Unit = {}
) {
    val detail by viewModel.detail.collectAsState()
    val error by viewModel.error.collectAsState()
    val busyAction by viewModel.busyAction.collectAsState()
    val setupRequired by viewModel.setupRequired.collectAsState()
    val reduced = reducedMotion()
    val calendarSync = rememberCalendarSyncController()
    val workspace: CurrentWorkspaceRepository = koinInject()
    var interactionExpanded by remember(id) { mutableStateOf(false) }
    var interactionKind by remember(id) { mutableStateOf(InteractionKind.WHATSAPP) }
    var interactionNote by remember(id) { mutableStateOf("") }
    var nextFollowUp by remember(id) { mutableStateOf<String?>(null) }
    var pickerPurpose by remember { mutableStateOf<PickerPurpose?>(null) }

    LaunchedEffect(id, reloadKey) { viewModel.load(id) }
    LaunchedEffect(viewModel, calendarSync) {
        viewModel.completedAction.collect { action ->
            when (action) {
                RelationshipDetailAction.INTERACTION -> {
                    interactionExpanded = false
                    interactionKind = InteractionKind.WHATSAPP
                    interactionNote = ""
                    nextFollowUp = null
                }
                RelationshipDetailAction.UPDATE -> Unit
                RelationshipDetailAction.MEETING -> {
                    val item = viewModel.detail.value
                    val workspaceId = workspace.currentWorkspaceId()
                    val start = item?.nextMeeting?.let { LocalDateTime.parse(it).toInstant(TimeZone.currentSystemDefault()).toEpochMilliseconds() }
                    if (item != null && workspaceId != null && start != null) {
                        calendarSync.submit(meetingCalendarKey(workspaceId, item.id), CalendarEventDraft(
                            title = "Reunião de apresentação · ${item.name}",
                            startsAtEpochMillis = start,
                            endsAtEpochMillis = start + 60 * 60 * 1000L
                        ))
                    }
                }
                RelationshipDetailAction.STAGE -> Unit
            }
        }
    }

    Scaffold(contentWindowInsets = WindowInsets.safeDrawing.only(WindowInsetsSides.Top + WindowInsetsSides.Horizontal), topBar = {
        PartejarTopBar(
            title = "Relacionamento",
            leadingContent = {
                PartejarIconButton(onClick = onBack) {
                    Icon(PartejarIcons.Actions.back, contentDescription = "Voltar")
                }
            }
        )
    }) { padding ->
        val phase = when {
            detail != null -> DetailPhase.CONTENT
            error != null -> DetailPhase.ERROR
            else -> DetailPhase.LOADING
        }
        AnimatedContent(
            targetState = phase,
            modifier = Modifier.fillMaxSize().padding(padding),
            transitionSpec = {
                val duration = if (reduced) PartejarMotion.press else PartejarMotion.feedback
                fadeIn(tween(duration, easing = PartejarMotion.enter)) togetherWith
                    fadeOut(tween(duration, easing = PartejarMotion.exit))
            },
            label = "relationshipDetailPhase"
        ) { target ->
            when (target) {
                DetailPhase.LOADING -> RelationshipDetailLoading()
                DetailPhase.ERROR -> RelationshipDetailLoadError(
                    message = error.orEmpty(),
                    onRetry = { viewModel.dismissError(); viewModel.load(id) }
                )
                DetailPhase.CONTENT -> detail?.let { item ->
                    RelacionamentoDetailContent(
                        item = item,
                        error = error,
                        busyAction = busyAction,
                        interactionExpanded = interactionExpanded,
                        interactionKind = interactionKind,
                        interactionNote = interactionNote,
                        nextFollowUp = nextFollowUp,
                        onEditPerson = onEditPerson,
                        onInteractionExpandedChange = {
                            if (it) viewModel.dismissError()
                            interactionExpanded = it
                        },
                        onInteractionKindChange = { interactionKind = it },
                        onInteractionNoteChange = { interactionNote = it },
                        onNextFollowUpChange = { nextFollowUp = it },
                        onPickFollowUp = { pickerPurpose = PickerPurpose.FOLLOW_UP },
                        onRegisterInteraction = { viewModel.interact(id, interactionKind, interactionNote, nextFollowUp) },
                        onScheduleMeeting = { pickerPurpose = PickerPurpose.MEETING },
                        onMove = { viewModel.move(id, it) },
                        onStartContract = { onStartContract(item.id) }
                    )
                }
            }
        }
    }

    pickerPurpose?.let { purpose ->
        PartejarDateTimePicker(onDismiss = { pickerPurpose = null },
            initialValue = if (purpose == PickerPurpose.MEETING) detail?.nextMeeting else nextFollowUp) { value ->
            when (purpose) {
                PickerPurpose.FOLLOW_UP -> nextFollowUp = value
                PickerPurpose.MEETING -> viewModel.scheduleMeeting(id, value, null)
            }
            pickerPurpose = null
        }
    }
    DeviceCalendarSyncDialogs(calendarSync)
    setupRequired?.let { requirements ->
        AlertDialog(
            onDismissRequest = viewModel::dismissSetupRequired,
            title = { Text("Prepare os cadastros do contrato") },
            text = { ContractSetupRequired(requirements,
                { viewModel.dismissSetupRequired(); onCreatePlan() },
                { viewModel.dismissSetupRequired(); onCreateContractTemplate() },
                modifier = Modifier.verticalScroll(rememberScrollState())) },
            confirmButton = { PartejarButton("Voltar", viewModel::dismissSetupRequired, variant = PartejarButtonVariant.TERTIARY,
                size = PartejarButtonSize.COMPACT, fillWidth = false, shape = PartejarShapes.pill) }
        )
    }
}

@Composable
internal fun RelationshipDetailLoading(modifier: Modifier = Modifier) {
    val spacing = PartejarTheme.spacing
    Column(
        modifier = modifier.fillMaxSize().padding(spacing.space16),
        verticalArrangement = Arrangement.spacedBy(spacing.space16)
    ) {
        androidx.compose.foundation.layout.Row(
            horizontalArrangement = Arrangement.spacedBy(spacing.space12)
        ) {
            PartejarSkeleton(Modifier.size(spacing.space64), PartejarShapes.pill)
            Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
                PartejarSkeletonText(Modifier.fillMaxWidth(0.76f).height(spacing.space24))
                PartejarSkeletonText(Modifier.fillMaxWidth(0.62f).height(spacing.space12))
                PartejarSkeletonText(Modifier.fillMaxWidth(0.72f).height(spacing.space20))
            }
        }
        PartejarCard(modifier = Modifier.fillMaxWidth()) {
            Column(verticalArrangement = Arrangement.spacedBy(spacing.space16)) {
                PartejarSkeletonText(Modifier.fillMaxWidth(0.38f).height(spacing.space16))
                androidx.compose.foundation.layout.Row(horizontalArrangement = Arrangement.spacedBy(spacing.space12)) {
                    PartejarSkeleton(Modifier.size(spacing.space64), PartejarShapes.radius14)
                    Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
                        PartejarSkeletonText(Modifier.fillMaxWidth(0.72f).height(spacing.space20))
                        PartejarSkeletonText(Modifier.fillMaxWidth(0.58f).height(spacing.space12))
                    }
                }
                PartejarSkeleton(Modifier.fillMaxWidth().height(spacing.space48), PartejarShapes.pill)
            }
        }
        PartejarCard(modifier = Modifier.fillMaxWidth()) {
            Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
                PartejarSkeletonText(Modifier.fillMaxWidth(0.52f).height(spacing.space16))
                repeat(3) {
                    androidx.compose.foundation.layout.Row(horizontalArrangement = Arrangement.spacedBy(spacing.space12)) {
                        PartejarSkeleton(Modifier.size(spacing.space24), PartejarShapes.pill)
                        PartejarSkeletonText(Modifier.weight(1f).height(spacing.space20))
                    }
                }
            }
        }
        PartejarCard(modifier = Modifier.fillMaxWidth()) {
            Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
                PartejarSkeletonText(Modifier.fillMaxWidth(0.44f).height(spacing.space16))
                repeat(2) { PartejarSkeletonText(Modifier.fillMaxWidth(0.82f).height(spacing.space20)) }
            }
        }
    }
}

@Composable
private fun RelationshipDetailLoadError(message: String, onRetry: () -> Unit) {
    val spacing = PartejarTheme.spacing
    Column(
        Modifier.fillMaxSize().padding(spacing.space24),
        verticalArrangement = Arrangement.spacedBy(spacing.space16)
    ) {
        PartejarAlert("Contato indisponível", PartejarAlertTone.ERROR) { Text(message) }
        PartejarButton("Tentar novamente", onRetry, motionEnabled = true)
    }
}

private enum class PickerPurpose { FOLLOW_UP, MEETING }
private enum class DetailPhase { LOADING, ERROR, CONTENT }
